import React from 'react';
import {
  getFilteredIdeas,
  addViews,
  listMembers,
  getViews,
  saveIdeaStarred, listOwners,
} from "../../Api";
import _ from "lodash";
import { getBagelId, updateAccessorByState } from "../../utils";
import { ActiveFilter } from "./filters/FiltersMenu";
import { Auth0ContextInterface, User } from "@auth0/auth0-react";
import { getNestedValue } from "../ideaNiv/Funcs";
import { showNotification } from "@mantine/notifications";
import { findFilterByName } from "../../context/helpers";

const PAGE_SIZE = 50;

interface SortStatus {
  columnAccessor?: string;
  direction?: string;
}

export const getFilteredRequests = (
  sortStatus: SortStatus,
  setLastFilterParams: React.Dispatch<React.SetStateAction<any>>,
  _page: string | null,
  search: string,
  activeFilters: ActiveFilter[],
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setIdeas: React.Dispatch<React.SetStateAction<any[]>>,
  setRequestsTotalCount: React.Dispatch<React.SetStateAction<number>>,
  setPage: React.Dispatch<React.SetStateAction<string | null>>,
  ideasFiltersConfig: {
    evidence:any,
    idea:any
  },
  tabName: string,
  auth0: Auth0ContextInterface<User>,
  isArchived: boolean,
  pathname: string, 
) => {
  const sort: any = {};
  if(sortStatus?.columnAccessor){
    sort[sortStatus?.columnAccessor] = sortStatus?.direction === "asc" ? -1 : 1;
  }

  setLastFilterParams((prevState: any) => {
    const params = {
      sort,
      limit: PAGE_SIZE,
      skip: ((Number(_page) || 1) - 1) * PAGE_SIZE,
      filters: {
        search,
        activeFilters,
      },
      pathname,
    };
    if (
      !(
        _.isEqual(params.sort, prevState.sort) &&
        _.isEqual(isArchived, prevState.isArchived) &&
        _.isEqual(params.skip, prevState.skip) &&
        _.isEqual(params.filters?.search, prevState.filters?.search) &&
        _.isEqual(params.filters?.activeFilters, prevState.filters?.activeFilters) &&
        params.pathname === prevState.pathname
      )
    ) {
      setLoading(true);
      const getNestedFilterFromChildFilters = (
        parentFilter: any | undefined,
        path: string
      ): any | undefined => {
        if (!parentFilter) return undefined;
      
        const pathParts = path.split('.'); // Split the path by dots
        let currentFilter: any = parentFilter;
      
        for (const part of pathParts.slice(1)) { // Skip the first part (parent filter)
          currentFilter = currentFilter?.childFilters?.[part];
          if (!currentFilter) return undefined;
        }
      
        return currentFilter;
      };
      
      const sanitizeFilter = (filter: Record<string, any>): Record<string, any> => {
        const { options, operations, childFilters, ...sanitizedFilter } = filter;
      
        // Recursively sanitize child filters
        if (childFilters && Object.keys(childFilters).length > 0) {
          sanitizedFilter.childFilters = Object.keys(childFilters).reduce(
            (acc: Record<string, any>, key: string) => {
              acc[key] = sanitizeFilter(childFilters[key]);
              return acc;
            },
            {}
          );
        }
      
        return sanitizedFilter;
      };
      
      getFilteredIdeas(
        {
          search,
          activeFilters: activeFilters?.map((filter) => {
            const evidenceFilter = ideasFiltersConfig['evidence']?.[filter.parent?.split('.')[0]]; // Get parent
            const ideaFilter = ideasFiltersConfig['idea']?.[filter.parent?.split('.')[0]]; // Get parent
      
            const nestedEvidenceFilter = getNestedFilterFromChildFilters(evidenceFilter, filter.parent);
            const nestedIdeaFilter = getNestedFilterFromChildFilters(ideaFilter, filter.parent);
      
            return {
              ...sanitizeFilter(nestedEvidenceFilter || {}),
              ...sanitizeFilter(nestedIdeaFilter || {}),
              ...filter,
            };
          }),
        },
        sort,
        PAGE_SIZE,
        ((Number(_page) || 1) - 1) * PAGE_SIZE,
        tabName, 
        auth0,
        isArchived
      )
        .then(({ totalCount, ideas }: { totalCount: number; ideas: any[] }) => {
          setIdeas(ideas);
          if(totalCount <= PAGE_SIZE){
            setPage('1')
          }
          setRequestsTotalCount(totalCount);
        })
        .catch((e) => console.log("filterRequests e:", e))
        .finally(() => setLoading(false));
      return params;
    } else {
      return prevState;
    }
  });
};

export const starClicked = (
  _id: string,
  bool: boolean,
  setLoadingStar: React.Dispatch<React.SetStateAction<any>>,
  idea: any,
  setIdeas: React.Dispatch<React.SetStateAction<any[]>>,
  auth0: Auth0ContextInterface<User>
) => {
  setLoadingStar((prev: any) => ({
    ...prev,
    [_id]: true,
  }));
  saveIdeaStarred(idea, auth0)
    .then(() => {
      setIdeas((prevState: any) => {
        const updated = prevState.map((item: any) => {
          if (item._id == _id) {
            const userId = getBagelId(auth0.user);
            return {
              ...item,
              starred: bool ? userId : false,
            };
          } else {
            return item;
          }
        });

        return updated;
      });
    })
    .catch(console.log)
    .finally(() =>
      setLoadingStar((prev: any) => ({
        ...prev,
        [_id]: false,
      }))
    );
};

export const loadViews = (
  deleted: boolean,
  setSavedViews: React.Dispatch<React.SetStateAction<any[]>>,
  selectedView: any,
  setSelectedView: React.Dispatch<React.SetStateAction<any>>,
  auth0: Auth0ContextInterface<User>
) => {
  getViews(auth0)
    .then(({ views }: any) => {
      setSavedViews(views);
      if (!selectedView?._id || deleted) {
        setSelectedView(views[0]);
      }
    })
    .catch((e) => {
      console.log(e);
    });
};

export const loadListMembers = (
  setMenuFiltersObj: React.Dispatch<React.SetStateAction<any>>,
  auth0: Auth0ContextInterface<User>
) => {
  listOwners(auth0)
    .then((members: any) => {
      setMenuFiltersObj((prev: any) => ({
        ...prev,
        Owner: {
          ...prev?.Owner,
          options: members.reduce(
            (acc: any, { _id, name }: { _id: string; name: string }) => {
              acc[_id] = name;
              return acc;
            },
            {}
          ),
        },
        Mentions: {
          ...prev?.Mentions,
          options: members.reduce(
            (acc: any, { _id, name }: { _id: string; name: string }) => {
              acc[_id] = name;
              return acc;
            },
            {}
          ),
        },
      }));
    })
    .catch((e) => {
      console.log(e);
    });
};

export const upsertViews = (
  body: any,
  func: (res: any) => void,
  auth0: Auth0ContextInterface<User>,
  setSavedViews: React.Dispatch<React.SetStateAction<any[]>>, // Add this line
  selectedView: any, // Add this line
  setSelectedView: React.Dispatch<React.SetStateAction<any>>, // Add this line
) => {
  addViews(body, auth0)
    .then((res: any) => {
      func(res);
      loadViews(
        body?.deleted,
        setSavedViews,
        selectedView,
        setSelectedView,
        auth0
      );
    })
    .catch((e) => {
      console.log(e);
    });
};

export const handleSaveViews = (
  viewName: string,
  isGlobal: boolean,
  activeFilters: ActiveFilter[],
  selectedView: any,
  setSelectedView: React.Dispatch<React.SetStateAction<any>>,
  showNotification: any,
  doClose: () => void,
  auth0: Auth0ContextInterface<User>,
  setSavedViews: React.Dispatch<React.SetStateAction<any[]>>, // Add this line
  isSubPageView: boolean,
) => {
  upsertViews(
    { name: viewName, filters: activeFilters, _id: selectedView._id, isGlobal, isSubPageView },
    (res) => {
      setSelectedView({
        _id: res?.data?.updateOneResult?.upsertedId,
        name: viewName,
        filters: activeFilters,
        isGlobal,
      });
      showNotification({
        title: "Saved successfully!",
        color: "teal",
        message: "view saved successfully",
        autoClose: 1200
        // icon: <IconCheck size="1.1rem" />,
      });
      doClose();
    },
    auth0,
    setSavedViews, // Add this line
    selectedView, // Add this line
    setSelectedView, // Add this line
  );
};




export const searchComponents = (text: string, components: any[]) => {
  if (typeof text !== 'string') {
    return;
  }

  const lowercaseText = text.toLowerCase();
  const filteredComponents: any[] = [];

  components.forEach((parent: any) => {
    const childComponents: any[] = parent.childComponents || [];

    // Check if the parent component matches the search text
    const isParentMatch = parent.name.toLowerCase().includes(lowercaseText);
    if (isParentMatch) {
      filteredComponents.push(parent);
    }

    // Filter the child components that match the search text
    const matchedChildren = childComponents.filter((child: any) =>
      child.name.toLowerCase().includes(lowercaseText)
    );

    // If there are any matched children, add the parent with the matched children to the filteredComponents
    if (matchedChildren.length > 0) {
      const parentWithMatchedChildren = { ...parent, childComponents: matchedChildren };
      filteredComponents.push(parentWithMatchedChildren);
    }
  });

  // Remove duplicates based on the parent's id
  const filteredComponentsWithoutDuplicates = Array.from(
    new Set(filteredComponents.map((c: any) => c._id)), // Use '_id' instead of 'id'
    (id:any) => {
      return filteredComponents.find((c: any) => c._id === id); // Use '_id' instead of 'id'
    }
  );

  return filteredComponentsWithoutDuplicates;
};

// export function searchFilter(obj: any, text: string): any {
//   if (typeof text !== 'string') {
//     return;
//   }

//   text = text.toLowerCase();

//   const result: any = {};
//   const allowedKeys = ['name', 'value', 'title']; 

//   const hasAllowedKeys = allowedKeys.some(key => key in obj);

//   if (Array.isArray(obj)) { // Check if obj is an array
//     obj.forEach((item: any, index: number) => {
//       if (typeof item === 'string') { // Check if the item is a string
//         if (item.toLowerCase().includes(text)) {
//           result[index] = item;
//         }
//       }
//     });
//   } else if(typeof obj == 'object'){
//     for (const key in obj) {
//       if (hasAllowedKeys) {
//         if (allowedKeys.includes(key) && typeof obj[key] === 'string') {
//           if (obj[key].toLowerCase().includes(text)) {
//             result[key] = obj[key];
//           // console.log(`Match found - Key: ${key}, Value: ${obj[key]}`);
//           }
//         }
//       } else {
//         if (typeof obj[key] === 'string') {
//           if (obj[key].toLowerCase().includes(text)) {
//             result[key] = obj[key];
//           // console.log(`Match found - Key: ${key}, Value: ${obj[key]}`);
//           }
//         } else if (typeof obj[key] === 'object' && obj[key] !== null) {
//           const subResult = searchFilter(obj[key], text);
//           if (Object.keys(subResult).length > 0) {
//             result[key] = subResult;
//           }
//         }
//       }
//     }
//   }
  
//   console.log('Searching in object:',{text, obj, result});

//   return result;
// }

export function searchFilter( items: any[], searchText: string): any[] {
  if (typeof searchText !== 'string' || searchText === "") {
    return items; // Return all items if no search text
  }

  const lowerCaseSearchText = searchText.toLowerCase();
  const allowedKeys = ['name', 'value', 'title']; // Define which keys are searchable

  // Filter items based on allowed keys and if the value includes the search text
  const result: any = items.filter(item => {
    return allowedKeys.some(key => {
      const itemValue = item[key as any];
      return typeof itemValue === 'string' && itemValue.toLowerCase().includes(lowerCaseSearchText);
    });
  });

  return result;
}

type QueryParams = {
  page?: string | null;
  filters?: string | null;
  selectedView?: string | null;
  sortStatus?: string | null;
};

export const buildQueryString = (page:string, filters:any, selectedView:string, sortStatus:string): string => {
  const queryParams: QueryParams = {
    page: page || null,
    filters: filters ? JSON.stringify(filters) : null,
    selectedView: selectedView ? JSON.stringify(selectedView) : null,
    sortStatus: sortStatus ? JSON.stringify(sortStatus) : null,
  };

  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]: any) => {
    if (value !== null) {
      searchParams.set(key, typeof value === 'string' ? value : encodeURIComponent(value));
    }
  });

  return searchParams.toString();
};

export const newBuildQueryString = (
  page?: string,
  filters?: any[],
  selectedView?: any,
  sortStatus?: string,
  ideasFiltersConfig?: { evidence: any; idea: any; }
): string => {
  // Build the basic query parameters with defaults.
  const queryParams: any = {
    page: page || null,
    selectedView: selectedView?.name ?? null,
    sortStatus: sortStatus ? JSON.stringify(sortStatus) : null,
  };

  // Process filters if provided.
  (filters || []).forEach(filter => {
    // If the filter doesn't have both filterObject and name and we have a config,
    // try to retrieve missing data via findFilterByName.
    if ((!filter.filterObject || !filter.name) && ideasFiltersConfig) {
      const parentKey = filter.parent || 'default';
      const filterConfig = findFilterByName(parentKey, 'all', ideasFiltersConfig);
      if (filterConfig) {
        filter.filterObject = filterConfig.filterObject;
        filter.name = filterConfig.name;
      }
    }

    // If still missing required data, skip this filter.
    if (!filter.filterObject || !filter.name) return;

    // Construct the key as 'filter.filterObject.name'
    const key = `filter.${filter.filterObject}.${filter.name}`;

    let value: any = null;
    // For number and boolean types, include operator with the active value.
    if (filter.type === 'number' || filter.type === 'boolean') {
      if (filter.operator && (filter.query !== null && filter.query !== undefined)) {
        value = { operator: filter.operator, value: filter.query };
      } else if (filter.operator && filter.selected !== undefined) {
        value = { operator: filter.operator, value: filter.selected };
      }
    } else {
      // For other filter types, use query if available; otherwise, use selected.
      if (filter.query !== null && filter.query !== undefined) {
        value = filter.query;
      } else if (filter.selected !== undefined) {
        value = filter.selected;
      }
    }

    // Add the filter value to queryParams if not null.
    if (value !== null) {
      queryParams[key] = typeof value === 'string' ? value : JSON.stringify(value);
    }
  });

  // Build the final query string from queryParams.
  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]: any) => {
    if (value !== null) {
      searchParams.set(key, value);
    }
  });

  return searchParams.toString();
};


export const parseFiltersFromQuery = (
  queryString: string,
  ideasFiltersConfig?: { evidence: any; idea: any; }
): any[] => {
  const searchParams = new URLSearchParams(queryString);
  const filters: any[] = [];

  searchParams.forEach((value, key) => {
    if (key.startsWith('filter.')) {
      // Remove the 'filter.' prefix and split the key into parts.
      const parts = key.replace('filter.', '').split('.');
      if (parts.length < 2) return;

      // The first part is the filterObject ("evidence" or "idea"), the rest is the filter name.
      const filterObject = parts[0] as "evidence" | "idea";
      const name = parts.slice(1).join('.');

      // Parse the stored value.
      let parsedValue: any;
      try {
        parsedValue = JSON.parse(value);
      } catch (e) {
        parsedValue = value;
      }

      // Start with a basic filter object.
      let filterData: any = {
        filterObject,
        name
      };

      // Retrieve filter configuration if provided.
      const filterConfig = ideasFiltersConfig
        ? findFilterByName(name, filterObject, ideasFiltersConfig)
        : null;

      // Use filterConfig to decide how to interpret the parsed value.
      if (filterConfig && (filterConfig.type === 'number' || filterConfig.type === 'boolean')) {
        // For number/boolean filters, we expect an object with an operator.
        if (
          typeof parsedValue === 'object' &&
          parsedValue !== null &&
          'operator' in parsedValue
        ) {
          filterData.operator = parsedValue.operator;
          filterData.query = parsedValue.value;
          // filterData.selected = parsedValue.value;
        } else {
          // Fallback: assign the parsed value to selected.
          filterData.selected = parsedValue;
        }
      } else {
        // For other filter types, simply assign the parsed value to selected.
        filterData.selected = parsedValue;
      }

      // Merge in the filter configuration if available.
      if (filterConfig) {
        filterData = { ...filterConfig, ...filterData };
      }

      filters.push(filterData);
    }
  });

  return filters;
};

export const calcTopValues = (ideasItems: any[], tableColsItems: any[]) => {
  const ideasColumnsCopy = structuredClone(tableColsItems);
  
  const updatedColumns = ideasColumnsCopy.map((barCol: any) => {
    const topValues = {
      assigned: 0,
      suggested: 0,
      total: 0,
    };
  
    if (barCol.renderComponent === 'BarCell') {
      const currentAccessor = barCol?.accessor;
  
      const assignedAccessor = updateAccessorByState(currentAccessor, 'assigned');
      const suggestedAccessor = updateAccessorByState(currentAccessor, 'suggested');
      const totalAccessor = updateAccessorByState(currentAccessor, 'total');
  
      ideasItems.forEach((ideaItem: any) => {
        const assignedVal = getNestedValue(ideaItem, assignedAccessor);
        if (assignedVal > topValues.assigned) {
          topValues.assigned = assignedVal;
        }
  
        const suggestedVal = getNestedValue(ideaItem, suggestedAccessor);
        if (suggestedVal > topValues.suggested) {
          topValues.suggested = suggestedVal;
        }
  
        const totalVal = getNestedValue(ideaItem, totalAccessor);
        if (totalVal > topValues.total) {
          topValues.total = totalVal;
        }
      });
  
      return {
        ...barCol,
        renderParams: {
          ...barCol.renderParams,
          topValues,
        },
      };
    }
  
    return barCol;
  });
  
  return updatedColumns;
};

export const validateComponentId = (componentId: string, allComponents: Array<any>, icon: any) => {
  if (!componentId) {
    showNotification({
      title: "Product Area Missing",
      color: "red",
      message:
        "Please assign a product area to this request before creating a new idea.",
      icon,
      autoClose: 1200,
    });
    return null;
  }

  const foundComponent = allComponents.find((c) => c._id === componentId);
  if (!foundComponent) {
    showNotification({
      title: "Invalid Product Area",
      color: "red",
      message:
        "Please assign a product area to this request before creating a new idea.",
      icon,
      autoClose: 1200,
    });
    return null;
  }

  return foundComponent;
};

export const buildNewIdeaObject = ( ownerId: string | null, chosenEvidence: any, componentId: string, defaultStatus: string, defaultPriority: string) => ({
  idea: {
    title: chosenEvidence?.title,
    description: chosenEvidence?.text,
    componentId,
    ownerId,
    rolloutDate: null,
    status: defaultStatus,
    priority: defaultPriority,
  },
  evidence: { ...chosenEvidence, ownerId },
}); 

export const getMergedColumns = (subPageLabel: string, ideaColumns: any[], subPage: any) => {
  // Only return columns that exist in subPageCol, merging with original properties
  return subPage.ideaColumns.map((subPageCol: any) => {
    const originalCol = ideaColumns.find(col => col.accessor === subPageCol.accessor);
    if (originalCol) {
      subPageCol.enabled = subPageCol.enabled == true ? (originalCol?.enabled || false) : (subPageCol.enabled || originalCol?.enabled || false);
      return {
        ...originalCol,  // Base properties from original column
        ...subPageCol,
      };
    } else {
      subPageCol.enabled  = subPageCol.enabled 
      return subPageCol;
    }
  });
};